import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import ChatComposer from "../components/ChatComposer";
import { auth, db } from "../services/firebase";
import { logUiEvent } from "../services/analytics";
import { sendDevChatMessage } from "../services/functionsClient";

function ChatDevPage() {
  const userId = auth.currentUser?.uid;
  const [chatId] = useState(() => uuidv4());
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const [pendingMessageId, setPendingMessageId] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [expandedMessages, setExpandedMessages] = useState({});
  const [model, setModel] = useState("gpt-5-mini-2025-08-07");
  const [toolChoice, setToolChoice] = useState("none");
  const [reasoningEffort, setReasoningEffort] = useState("disable");

  const modelOptions = useMemo(
    () => [
      { value: "gpt-5-mini-2025-08-07", label: "gpt-5-mini-2025-08-07" }
    ],
    []
  );

  const chatRef = useMemo(() => {
    if (!userId) return null;
    return doc(db, "users", userId, "chats", chatId);
  }, [chatId, userId]);

  useEffect(() => {
    if (!chatRef) return undefined;

    const unsubscribe = onSnapshot(
      chatRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const nextMessages = snapshot.data()?.messages || [];
          setMessages(nextMessages);

          if (pendingMessageId) {
            const hasAssistant = nextMessages.some(
              (item) =>
                item.role === "assistant" &&
                item.createdAt &&
                new Date(item.createdAt).getTime() >= new Date(pendingMessageId).getTime()
            );

            if (hasAssistant) {
              setPending(false);
              setPendingMessageId(null);
              setStatus("");
            }
          } else {
            setStatus("");
          }
        } else {
          setDoc(chatRef, { messages: [] });
        }
      },
      (snapshotError) => {
        setError(snapshotError.message);
      }
    );

    return unsubscribe;
  }, [chatRef, pendingMessageId]);

  const handleSend = async ({ prompt, model: selectedModel, reasoningEffort: selectedEffort, toolChoice: selectedTool }) => {
    if (!chatRef || !userId) {
      setError("You must be signed in to chat.");
      return;
    }

    const timestamp = new Date().toISOString();
    const newMessage = {
      role: "user",
      content: prompt,
      createdAt: timestamp,
      model: selectedModel,
      reasoningEffort: selectedEffort,
      toolChoice: selectedTool
    };

    setPending(true);
    setPendingMessageId(timestamp);
    setError("");
    setStatus("Sending message...");

    try {
      const nextMessages = [...messages, newMessage];
      await setDoc(chatRef, { messages: nextMessages }, { merge: true });
      await sendDevChatMessage({
        userId,
        chatId,
        message: prompt,
        model: selectedModel,
        reasoningEffort: selectedEffort,
        toolChoice: selectedTool
      });
      logUiEvent("dev_chat_message_sent", { length: prompt.length });
      setStatus("Waiting for response from Firebase Function...");
    } catch (sendError) {
      setError(sendError.message);
      setStatus("");
    } finally {
      // keep pending true until Firestore delivers the assistant message
    }
  };

  const toggleDetails = (id) => {
    setExpandedMessages((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="grid" style={{ gap: "16px" }}>
      <div className="panel">
        <h2 className="section-title">Chat (Dev)</h2>
        <p className="muted">
          Send test prompts through LiteLLM and persist the transcript in
          Firestore at <code>users/{userId || "<userid>"}/chats/{chatId}</code>.
        </p>
      </div>

      <div className="panel">
        <h3 className="section-title">New message</h3>
        <ChatComposer
          onSend={handleSend}
          disabled={pending}
          model={model}
          availableModels={modelOptions}
          onModelChange={setModel}
          reasoningEffort={reasoningEffort}
          onReasoningEffortChange={setReasoningEffort}
          toolChoice={toolChoice}
          onToolChoiceChange={setToolChoice}
        />
        {status && <p className="muted" style={{ marginTop: "8px" }}>{status}</p>}
        {error && <p className="error" style={{ marginTop: "8px" }}>{error}</p>}
      </div>

      <div className="panel">
        <h3 className="section-title">Chat history</h3>
        <div className="list">
          {messages.map((item, index) => (
            <div key={`${item.createdAt}-${index}`} className="list__item">
              <div className="badge">{item.role}</div>
              <p>{item.content}</p>
              {item.role === "assistant" && (item.cost || item.thoughts) && (
                <div style={{ marginTop: "8px" }}>
                  <button className="button button--ghost" type="button" onClick={() => toggleDetails(`${item.createdAt}-${index}`)}>
                    {expandedMessages[`${item.createdAt}-${index}`] ? "Hide details" : "View cost & thoughts"}
                  </button>
                  {expandedMessages[`${item.createdAt}-${index}`] && (
                    <div className="muted" style={{ marginTop: "8px" }}>
                      {item.cost != null && <p>Response cost: {item.cost.toFixed ? item.cost.toFixed(4) : item.cost}</p>}
                      {item.thoughts && (
                        <pre style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(item.thoughts, null, 2)}</pre>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {pending && (
            <div className="list__item">
              <div className="badge">assistant</div>
              <p className="muted">Generating response...</p>
            </div>
          )}
          {messages.length === 0 && <p className="muted">No chat messages yet.</p>}
        </div>
      </div>
    </div>
  );
}

export default ChatDevPage;
